import React from "react";
import { Alert, TabContent, TabPane } from "reactstrap";

import useProfile from "./hooks/useProfile";

import ProfileHeader from "./components/Header/ProfileHeader";
import ProfileMyData from "./components/Panels/ProfileMyData";
import ProfileTabList from "./components/ProfileTabList";
import ProfileEvaluation from "./components/Panels/ProfileEvaluation";
import ProfileAttendance from "./components/Panels/ProfileAttendance";
import MembershipActionsModal from "./components/Membership/MembershipActionsModal";
import { ContentLoader } from "../../../components/Common";
import HistoricalReceiptModal from "../../../components/Common/HistoricalReceiptModal";

/**
 * MemberProfile
 * 
 * Ponto de entrada do módulo de Perfil do Membro.
 * Componente "burro" — responsável apenas por renderização.
 * Toda a lógica de negócio está delegada ao useProfile (hooks/).
 */
const MemberProfile = () => {
    const profile = useProfile();

    if (profile.loading) {
        return <ContentLoader loading={true} message="Carregando perfil..." />;
    }

    if (profile.error) {
        return (
            <Alert color="danger" className="text-center">
                <i className="mdi mdi-alert-circle-outline font-size-24 d-block mb-2"></i>
                {profile.error}
            </Alert>
        );
    }

    return (
        <React.Fragment>
            <ProfileHeader
                member={profile.isEditing ? profile.formik.values : profile.member}
                onEdit={profile.handleEdit}
                onSave={profile.handleSave}
                onCancel={profile.handleCancelEdit}
                isEditing={profile.isEditing}
                onPhotoChange={profile.handlePhotoChange}
                onNewSale={profile.handleNewSale}
                onPayDebt={profile.handlePayDebt}
                totalDebt={profile.totalDebt}
                activeTab={profile.activeTab}
                toggleTab={profile.toggleTab}
            />

            <div className="mt-4">
                <TabContent activeTab={profile.activeTab}>
                    <TabPane tabId="1">
                        <div className="py-2">
                            <ProfileMyData isEditing={profile.isEditing} formik={profile.formik} />
                        </div>
                    </TabPane>
                    <TabPane tabId="2">
                        <ProfileTabList
                            title="Histórico Financeiro"
                            description="Acompanhe todas as cobranças, pagamentos e mensalidades do aluno."
                            icon="mdi-cash-multiple"
                            columns={profile.financialColumns}
                            data={profile.financials}
                            isLoading={profile.financialsLoading}
                            emptyMessage="Nenhum registro financeiro encontrado."
                        />
                    </TabPane>
                    <TabPane tabId="attendance">
                        <ProfileAttendance member={profile.member} />
                    </TabPane>
                    <TabPane tabId="3">
                        <ProfileTabList
                            title="Matrículas"
                            description="Histórico completo de matrículas ativas e canceladas."
                            icon="mdi-school-outline"
                            columns={profile.enrollmentColumns}
                            data={profile.enrollments}
                            isLoading={profile.enrollmentsLoading}
                            emptyMessage="Nenhuma matrícula encontrada."
                            emptySubMessage="Clique em 'Nova Matrícula' para matricular o aluno em uma turma."
                            actionButton={
                                <button className="btn btn-primary btn-sm rounded-0" onClick={profile.handleNewEnrollment}>
                                    <i className="mdi mdi-plus me-1"></i>Nova Matrícula
                                </button>
                            }
                        />
                    </TabPane>
                    <TabPane tabId="4">
                        <ProfileTabList
                            title="Planos"
                            description="Planos adquiridos pelo aluno, com vigência e valores."
                            icon="mdi-file-document-outline"
                            columns={profile.membershipColumns}
                            data={profile.memberMemberships}
                            isLoading={profile.membershipsLoading}
                            emptyMessage="Nenhum plano encontrado."
                            emptySubMessage="Os planos são criados automaticamente ao realizar uma venda."
                        />
                    </TabPane>
                    <TabPane tabId="5">
                        <div className="py-5 text-center text-muted">
                            <h5>Testes</h5>
                        </div>
                    </TabPane>
                    <TabPane tabId="6">
                        <ProfileEvaluation member={profile.member} />
                    </TabPane>
                </TabContent>
            </div>

            {/* Modal de ações do MemberMembership */}
            <MembershipActionsModal
                isOpen={profile.isActionsModalOpen}
                toggle={profile.toggleActionsModal}
                membership={profile.selectedMembership}
                onRefresh={profile.handleMembershipRefresh}
            />

            {/* Modal de Recibo Histórico */}
            {profile.receiptModalOpen && profile.currentReceiptData && (
                <HistoricalReceiptModal
                    isOpen={profile.receiptModalOpen}
                    toggle={profile.toggleReceiptModal}
                    saleData={profile.currentReceiptData}
                    memberName={profile.member.fullName}
                />
            )}
        </React.Fragment>
    );
};

export default MemberProfile;
